import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { constants, getPrefixedAddressFields, getGqlQuery,
  snowplowContext, localStorage } from 'common/helpers'
import { getCookieValue } from 'helpers'

import setCCPA from './setCCPA.graphql'
import userMeQuery from './userMeQuery.graphql'
import userStatsQuery from './userStatsQuery.graphql'
import userPaymentQuery from './userPaymentQuery.graphql'
import billingAndShippingQuery from './billingAndShippingQuery.graphql'
import setAddressConfirmedMutation from './setAddressConfirmed.graphql'
import saveNetflixRecommendations from './saveNetflixRecommendations.graphql'
import setOnboardingProductCategoriesMutation from './setOnboardingProductCategories.graphql'


const _updateGender = (gender, withSelectedGender) => {
  if ([ 'female', 'male' ].indexOf(gender) === -1) {
    console.warn(`Unable to update user gender to: ${gender}`)
  }
  else {
    actions.misc.cookies.setItem(constants.cookieNames.userGender, gender)
    reducers.models.me.update({
      gender,
      selectedGender: withSelectedGender ? gender : undefined,
    })
  }
}

/**
 * Update gender in redux
 *
 * @param {String} gender
 */
const updateGender = (gender) => _updateGender(gender, false)

/**
 * Update gender in redux in case of the user's action (set selectedGender)
 *
 * @param {String} gender
 */
const updateSelectedGender = (gender) => _updateGender(gender, true)

const removeSelectedGender = () => reducers.models.me.removeSelectedGender()

/**
 * Sets new user profile and track login
 * @param profile
 */
const set = (profile) => {
  const { firstName, lastName, email, paymentOption } = profile

  reducers.models.me.update({ ...profile, isLoggedIn: true })

  // TODO revise when ssr is implemented
  actions.models.payment.update({ paymentOption })
}

/**
 * Fetches user profile
 *
 * @param {Promise} [awaitFor]
 */
const fetch = (awaitFor) =>
  request.post('graphql?name=userMe', {
    body: {
      query: userMeQuery,
    },
    modifyResult: (data) => data.user.me,
  })
    .then((profile) => {
      if (awaitFor) {
        awaitFor.then(() => {
          set(profile)
        })
      }
      else {
        set(profile)
      }

      return profile
    })

const fetchStats = () =>
  request.post('graphql?name=userStat', {
    body: {
      query: userStatsQuery,
    },
    modifyResult: ({ user: { me: { stats } } }) => (
      stats || {
        orders: 0,
        queue: 0,
        reviews: 0,
        ratedProducts: 0,
      }),
  })
    .then((stats) => reducers.models.me.update({ stats }))

const fetchRedeemCoupon = () =>
  request.post('graphql?name=redeemCoupon', {
    body: {
      query: `{
        redeemCoupon {
          code
          coupon
          type
          shouldCharge
          chargeAmount
          months
        }
      }`,
    },
  })
    .then(({ redeemCoupon }) => {
      updateRedeemCoupon(redeemCoupon)
    })

const fetchBillingAndShippingInfo = () =>
  request.post('graphql?name=billingAndShipping', {
    body: {
      query: billingAndShippingQuery,
    },
    modifyResult: (data) => data.user.me,
  })
    .then((result) => {
      if (result) {
        reducers.models.me.update(result)
      }

      return result
    })

const confirmShippingInfo = () =>
  request.post('graphql?name=setAddressConfirmed', {
    body: {
      query: setAddressConfirmedMutation,
    },
    withNestedErrors: true,
  })
    .then(() => actions.models.subscription.fetch())

const fetchCurrentPaymentMethodInfo = () =>
  request.gql('currentPaymentMethod', {
    body: {
      query: userPaymentQuery,
    },
    modifyResult: (data) => data.user.me,
  })
    .then((result) => {
      reducers.models.me.update(result)
      return result
    })

const update = (values) =>
  request.post('rest/user/profile', {
    body: values,
  })
    .then((result) => {
      reducers.models.me.update(result)
      return result
    })

const updateFinishedQuiz = (hasFinishedQuiz) =>
  reducers.models.me.update({ hasFinishedQuiz })

/**
 *
 * @param {String} type - "onboarding" || "recommendations" || "subscription"
 * @param {Boolean} value
 * @returns {*}
 */
const updateFinishedQuizNew = (type, value) =>
  reducers.models.me.updateFinishedQuiz({ type, value })

// Update shipping and billing

const updateShippingAndBillingInfo = (data) =>
  request.post('rest/user/address', {
    body: {
      shippingFirstName:    data.shipping.firstName,
      shippingLastName:     data.shipping.lastName,
      shippingStreet:       data.shipping.street,
      shippingApt:          data.shipping.apt,
      shippingZipCode:      data.shipping.zipCode,
      shippingState:        data.shipping.state,
      shippingCity:         data.shipping.city,
      telephone:            data.shipping.telephone,
      billingFirstName:     data.billing.firstName,
      billingLastName:      data.billing.lastName,
      billingStreet:        data.billing.street,
      billingApt:           data.billing.apt,
      billingZipCode:       data.billing.zipCode,
      billingState:         data.billing.state,
      billingCity:          data.billing.city,
    },
    tokenName: 'addressUpdate',
  })
    .then(() => {
      reducers.models.me.update(data)

      // always refetch subscription
      return actions.models.subscription.fetch()
    })
    .then(() => data, () => Promise.reject())

const setAgeAndSkinType = ({ skinType = null, ageCategory = null }) => {
  reducers.models.me.setAgeAndSkinType({ ageCategory, skinType })
}

const updateShippingInfoInState = (data) =>
  reducers.models.me.update({ shipping: { ...data } })

const updateShippingInfo = (data) => {
  const { models: { subscription: isUndeliverable } } = getState()

  return request.post('rest/user/address/shipping', {
    body: {
      shippingFirstName:    data.firstName,
      shippingLastName:     data.lastName,
      shippingStreet:       data.street,
      shippingApt:          data.apt,
      shippingZipCode:      data.zipCode,
      shippingState:        data.state,
      shippingCity:         data.city,
      telephone:            data.telephone,
    },
    tokenName: 'addressUpdate',
  })
    .then(() => {
      if (isUndeliverable) {
        actions.models.subscription.fetch()
      }
      updateShippingInfoInState(data)

      return data
    })
}

const updateBillingInfo = (data) => {
  const { models: { subscription: isUndeliverable } } = getState()

  return request.post('rest/user/address/billing', {
    body: {
      billingFirstName:     data.firstName,
      billingLastName:      data.lastName,
      billingStreet:        data.street,
      billingApt:           data.apt,
      billingZipCode:       data.zipCode,
      billingState:         data.state,
      billingCity:          data.city,
      telephone:            data.telephone,
    },
    tokenName: 'addressUpdate',
  })
    .then(
      () => {
        if (isUndeliverable) {
          actions.models.subscription.fetch()
        }
        reducers.models.me.update({ billing: data })
        return data
      },
      () => Promise.reject()
    )
}

const changeGender = (gender) =>
  request.post('graphql', {
    withErrorModal: true,
    body: {
      query: `
        mutation {
          changeGender(
            gender: ${gender}
          )
        }
      `,
    },
  })
    .then(() => {
      _updateGender(gender, true)
    })


// Update card info

const beginUpdateCreditCardInfo = (data) => {
  const { routing: { location: { pathname, query: { resubscribeCode: queryResubscribeCode } } } } = getState()
  const { shipping = {}, billing = {}, isPaypal, plan, paymentMethod, applePayValidationURL, placement } = data

  const formValues = getPrefixedAddressFields({ shipping, billing })
  const resubscribeCode = getCookieValue(constants.cookieNames.resubscribeCode) || queryResubscribeCode

  // we can't use CardUpdatePageNew without promo, but it's required in a new resubscribe flow, so here is this duct-tape
  const promo = resubscribeCode === constants.resubscribeTypes.resubfreeproduct && data.promo === 'UPDCARDFREEPRODUCT' ? undefined : data.promo

  // we have a flow with redirect to card-update if resubscribe was failed (from backend) SB-1213
  // check pathname as fool-proof of update from profile

  const isResubscribeCodeAllowed = Boolean(resubscribeCode && /card-update/.test(pathname))
  const isFreeCase = /case-card-update/.test(pathname) || isResubscribeCodeAllowed

  const { page, pageType } = snowplowContext.get()
  const snowplowMetadata = { page, pageType }

  return request.post('rest/card-update/begin', {
    body: {
      ...formValues,
      isPaypal,
      plan,
      paymentType: paymentMethod, // TODO ask backend rename `paymentType` to `paymentMethod`
      freeCase: isFreeCase,
      promo,
      placement,
      applePayValidationURL,
      snowplowMetadata,
      resubscribeCode: isResubscribeCodeAllowed ? resubscribeCode : undefined,
    },
    tokenName: 'cardUpdate',
  })
}

const commitUpdatePaymentError = (data) => {
  const { message, title, statusCode, resData } = data
  const { models: { subscription: { currPlan: { shippingPeriod, billingPeriod } } } } = getState()
  const { authCode: authCodeString } = resData || {}

  // Error codes https://github.com/scentbird/scnt-payment-gateway/blob/master/src/main/groovy/com/scentbird/gateway/vindicia/AuthCode.groovy
  const authCode            = parseInt(authCodeString, 10)
  const isPrepaidCardError  = authCode === 309

  const isPrepaidCardModalVisible = (
    shippingPeriod === 1
    && billingPeriod < 12
    && isPrepaidCardError
    && !getCookieValue(constants.cookieNames.isPrepaidCardWithSubscriptionPlansModalHidden)
  )

  // TODO add PrepaidCardWithDiscountModal as on old flow
  if (isPrepaidCardModalVisible) {
    actions.models.modals.open(constants.modals.prepaidCard.PrepaidCardWithSubscriptionPlans, {
      onChoose: (subscriptionPlanToUpgrade) => actions.models.payment.update({ subscriptionPlanToUpgrade }),
    })
  }
  else if (statusCode === 409) {
    if (message && title) {
      actions.models.modals.open(constants.modals.Dialog, {
        title,
        content: message,
        buttonTitle: 'Got it',
      })
    }

    actions.models.payment.update({ subscriptionPlanToUpgrade: null })
  }
  else {
    actions.models.modals.open(constants.modals.ErrorCard)
  }

  return Promise.reject()
}

const updateOrderInfo = (data) => {
  const { models: { subscription: isUndeliverable } } = getState()

  if (!data) {
    return
  }

  const { subscriptionOrderId, subscriptionPrice, subscriptionStatus, chargeReason } = data

  if (isUndeliverable) {
    actions.models.subscription.fetch()
  }
  else if (subscriptionStatus) {
    actions.models.subscription.updateStatus(subscriptionStatus)
  }

  if (subscriptionOrderId && subscriptionPrice) {
    reducers.models.payment.update({
      subscriptionOrderId,
      subscriptionPrice,
      subscriptionPlanToUpgrade: null,
      isUnpause: chargeReason === 'unpause',
    })
  }
}

const commitUpdatePaymentMethodInfo = (data) => {
  const { page, pageType } = snowplowContext.get()
  const snowplowMetadata = { page, pageType }

  return request.post('rest/card-update/commit', {
    body: {
      ...data,
      snowplowMetadata,
    },
    tokenName: 'cardUpdate',
    timeout: {
      response: 120 * 1000, // wait 120 sec
    },
    withErrorModal: false,
  })
    .then(updateOrderInfo, commitUpdatePaymentError)
    .then(() => {
      actions.misc.cookies.removeItem(constants.cookieNames.resubscribeCode)

      return fetch()
    })
}

/**
 *
 * @param data
 * @param {Object} data.billing
 * @param {Object} data.creditCard
 */
// TODO remove promo and plan from commit when backend will be ready
const updateCreditCardInfo = (data) => {
  const { billing, shipping, promo, subscriptionPlanToUpgrade: plan, placement } = data

  return beginUpdateCreditCardInfo({
    placement,
    shipping,
    billing,
    promo,
    plan,
    paymentMethod: constants.paymentMethods.creditCard,
  })
    .then(
      ({ gatewaySessionId, vwsArgs, cardUpdateSession }) => (
        actions.models.payment.callVindicia({ ...data, gatewaySessionId, vwsArgs })
          .then(() => commitUpdatePaymentMethodInfo({ cardUpdateSession }))
          .then(() => reducers.models.me.update({ billing }))
          .then(() => reducers.models.payment.update({ subscriptionPlanToUpgrade: null }))
      ),
      () => Promise.reject()
    )
}

/**
 *
 * @param {object} data
 * @param {object} data.shipping
 * @param {object} data.billing
 * @param {string} data.promo
 */
const beginUpdatePayPalInfo = (data) => {
  const { shipping, billing, promo, placement } = data

  return beginUpdateCreditCardInfo({
    placement,
    shipping,
    billing,
    promo,
    paymentMethod: constants.paymentMethods.payPal,
    isPaypal: true,
  })
    .then(
      ({ paypalToken, cardUpdateSession }) => {
        actions.models.payment.update({ cardUpdateSession })

        return paypalToken
      },
      () => Promise.reject()
    )
}

const beginUpdateApplePayInfo = ({ applePayValidationURL, session, placement }) => {
  const { models: { me: { billing } } } = getState()

  return beginUpdateCreditCardInfo({
    billing,
    placement,
    paymentMethod: constants.paymentMethods.applePay,
    applePayValidationURL,
  })
    .then(
      ({ token, applePaySession, cardUpdateSession }) => {
        actions.models.requestTokens.update({ applePayPaymentToken: token })
        actions.models.payment.update({ cardUpdateSession })

        session.completeMerchantValidation(applePaySession)
      },
      ({ error: errorCode }) => {
        let error = new window.ApplePayError('unknown')

        if (errorCode === 'Unauthorized') {
          error = new window.ApplePayError('unknown', 'emailAddress')
        }

        session.completeMerchantValidation({
          errors: [ error ],
          newTotal: {
            label: 'Scentbird',
            amount: '0.00',
          },
        })

        return Promise.reject()
      }
    )
}

const commitUpdatePayPalInfo = (data) => {
  const { models: { payment: { cardUpdateSession } } } = getState()

  return commitUpdatePaymentMethodInfo({ ...data, cardUpdateSession })
}

const commitUpdateApplePayInfo = (session, { shipping, billing, applePayPaymentToken, token }) => {
  const { models: { payment: { cardUpdateSession } } } = getState()

  const telephone = shipping.telephone || billing.telephone

  let formValues = getPrefixedAddressFields({ shipping, billing })

  const { page, pageType } = snowplowContext.get()
  const snowplowMetadata = { page, pageType }

  return request.post('rest/card-update/commit', {
    body: {
      ...formValues,
      telephone,
      applePayPaymentToken,
      cardUpdateSession,
      token,
      snowplowMetadata,
    },
    tokenName: 'cardUpdate',
    timeout: {
      response: 120 * 1000, // wait 120 sec
    },
    withErrorModal: false,
  })
    .then(
      (response) => {
        if (session.completePayment) {
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
        }

        actions.misc.cookies.removeItem(constants.cookieNames.resubscribeCode)

        updateOrderInfo(response)

        return Promise.all([
          updateShippingAndBillingInfo({ shipping, billing }),
          fetch(),
        ])
      },
      ({ err }) => {
        if (session.completePayment) {
          session.completePayment(window.ApplePaySession.STATUS_FAILURE)
        }

        throw new Error(err)
      })
}

const sendBloggerInfo = (data) => {
  const {
    name, email, country, bloglink, readers, vloglink, subscribers,
    facebook, pinterest, twitter, instagram, comments,
  } = data

  const bloggerRequestQuery = getGqlQuery('submitBloggerRequest', {
    input: {
      name,
      email,
      country,
      bloglink,
      readers,
      vloglink,
      subscribers,
      facebook,
      twitter,
      pinterest,
      instagram,
      comments,
    },
  })

  return request.post('graphql?name=bloggerRequest', {
    body: {
      query: `mutation {
        ${bloggerRequestQuery} {
          result
        }
      }`,
    },
  })
}

const removeStorageData = () => {
  actions.misc.cookies.removeItem(constants.cookieNames.offer)
  actions.misc.cookies.removeItem(constants.cookieNames.discountCoupon)
  // TODO remove cookies.clear
  // cookies.clear()
}

const getInvitedFriends = () =>
  request.get('rest/user/stats/invited')
    .then((data) => reducers.models.me.update({ invitedFriends: data }))

const inviteUser = (emails) =>
  request.post('rest/user/invite', {
    body: {
      emails,
    },
  })
    .then(() => getInvitedFriends())

const startInviteTracking = () => request.get('rest/user/tracking/start')

const stopInviteTracking = () => request.get('rest/user/tracking/end')

const getReferralCodes = () => {
  const { models: { me: { referralCodes } } } = getState()

  if (referralCodes) {
    return Promise.resolve()
  }

  return request.get('rest/user/referral-codes')
    .then((data) => reducers.models.me.update({ referralCodes: data }))
}


/**
 *
 * @param values
 * @returns {Promise<{
 *   isAlreadyValidated?: boolean,
 *   recommendedValues?: {
 *     country?: string,
 *     firstName?: string,
 *     lastName?: string,
 *     apt?: string,
 *     city?: string,
 *     state?: string,
 *     street?: string,
 *     zipCode?: string,
 *   }
 * }>}
 */
const validateAddress = (values) => {
  const { firstName, lastName, street, zipCode, city, state, apt, onlyCleansedAddress = false } = values

  // TODO @grammka ask back to rework this fields
  const data = {
    firstname: firstName,
    lastname: lastName,
    street1: street,
    street2: apt,
    postcode: zipCode,
    city,
    region: state,
    // flag needed if user already has address, it used on addressValidation modal on thankYou page
    // needed to prevent isAlreadyValidated in the response
    onlyCleansedAddress,
  }

  return request.post('rest/user/address/validate', {
    body: data,
    withErrorModal: false,
    modifyResult: ({ data, isAlreadyValidated }) => {
      let result = {
        isAlreadyValidated,
      }

      if (data && Object.keys(data).length) {
        const addressKeyMap = {
          country: 'country',
          firstname: 'firstName',
          lastname: 'lastName',
          shippingApt: 'apt',
          shippingCity: 'city',
          shippingState: 'state',
          shippingStreet: 'street',
          shippingZipCode: 'zipCode',
        }

        const recommendedValues = {}

        Object.keys(data).forEach((key) => {
          recommendedValues[addressKeyMap[key]] = data[key]
        })

        if (Object.keys(recommendedValues).length) {
          result.recommendedValues = recommendedValues
        }
      }

      return result
    },
  })
}

const updateRedeemCoupon = (redeemCoupon) => reducers.models.me.update({ redeemCoupon })

const updateRegisteredStatus = (isRegistered) => reducers.models.me.update({ isRegistered })

const setShippingValues = reducers.models.me.setShippingValues

const setOnboardingProductCategories = (categories) =>
  request.post('graphql?name=setOnboardingProductCategories', {
    body: {
      query: setOnboardingProductCategoriesMutation,
      variables: {
        input: { categories },
      },
    },
    withNestedErrors: true,
  })
    .then(() => {
      reducers.models.me.update({ onboardingProductCategories: categories })
    })

const setDoNotSellMyPersonalInfo = () =>
  request.post('graphql?name=setDoNotSellMyPersonalInfo', {
    body: {
      query: setCCPA,
      variables: {
        input: { enabled: true },
      },
    },
    withNestedErrors: true,
  })

const saveNetflixRecommendationsLikes = () => {
  const { items } = localStorage.getSessionItem(constants.localStorageNames.netflixRecommendations) || {}

  // for analytics only
  if (items) {
    localStorage.removeSessionItem(constants.localStorageNames.netflixRecommendations)
    actions.misc.cookies.setSessionItem(constants.cookieNames.mainPageThxNotification, 'recommendations')

    request.post('graphql?name=saveNetflixRecommendations', {
      withErrorModal: false,
      body: {
        query: saveNetflixRecommendations,
        variables: {
          input: {
            votes: items.map(({ id }) => ({
              productId: id,
              status: 'POSITIVE',
            })),
          },
        },
      },
    })
  }
}


export default {
  updateGender,
  updateSelectedGender,
  removeSelectedGender,
  fetch,
  update,
  set,
  fetchStats,
  fetchCurrentPaymentMethodInfo,
  updateFinishedQuiz,
  fetchBillingAndShippingInfo,
  getInvitedFriends,
  getReferralCodes,
  setAgeAndSkinType,
  updateShippingAndBillingInfo,
  updateShippingInfo,
  confirmShippingInfo,
  updateShippingInfoInState,
  updateBillingInfo,
  updateCreditCardInfo,
  beginUpdatePayPalInfo,
  commitUpdatePayPalInfo,
  commitUpdateApplePayInfo,
  beginUpdateApplePayInfo,
  sendBloggerInfo,
  removeStorageData,
  inviteUser,
  startInviteTracking,
  stopInviteTracking,
  validateAddress,
  updateRedeemCoupon,
  updateRegisteredStatus,
  fetchRedeemCoupon,
  changeGender,
  updateFinishedQuizNew,
  setOnboardingProductCategories,
  setShippingValues,
  setDoNotSellMyPersonalInfo,
  saveNetflixRecommendationsLikes,
}
